import { useState, useEffect, useRef, useCallback } from 'react';
import { Socket, Channel } from 'phoenix';

interface UseBolComplianceReportOptions {
  userId: string;
  jwtToken: string;
  websocketUrl: string;
  onDownload?: () => void;
}

interface CacheStatus {
  status: 'cached' | 'processing' | 'none' | 'checking';
  cached_at?: string;
  age_seconds?: number;
  row_count?: number;
  presigned_url?: string;
  job_uuid?: string;
  started_at?: string;
}

interface BolReportState {
  isGenerating: boolean;
  cacheStatus: CacheStatus;
  error: string | null;
}

const POLL_INTERVAL_MS = 2000;
const MAX_POLL_ITERATIONS = 30;

export function useBolComplianceReport(options: UseBolComplianceReportOptions) {
  const { userId, jwtToken, websocketUrl, onDownload } = options;

  const onDownloadRef = useRef(onDownload);
  onDownloadRef.current = onDownload;

  const [state, setState] = useState<BolReportState>({
    isGenerating: false,
    cacheStatus: { status: 'none' },
    error: null,
  });

  const socketRef = useRef<Socket | null>(null);
  const channelRef = useRef<Channel | null>(null);
  const currentJobRef = useRef<string | null>(null);
  const currentFacilityRef = useRef<number | null>(null);
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollCountRef = useRef(0);

  const clearPolling = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    pollCountRef.current = 0;
  }, []);

  // Auto-download helper
  const autoDownload = useCallback((url: string) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = `bol_compliance_report_${Date.now()}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, []);

  // Initialize socket and channel
  useEffect(() => {
    if (!userId || !jwtToken) return;

    const socket = new Socket(websocketUrl, {
      params: { token: jwtToken },
    });

    socket.connect();
    socketRef.current = socket;

    const channel = socket.channel(`reports:${userId}`, {});

    channel.join()
      .receive('ok', () => {
        console.log('Connected to reports channel (BOL compliance)');
      })
      .receive('error', (err: any) => {
        console.error('Failed to join reports channel:', err);
        setState(prev => ({
          ...prev,
          error: 'Failed to connect to report service',
        }));
      });

    channelRef.current = channel;

    // Listen for completion
    channel.on('report_completed', (payload: any) => {
      if (payload.job_uuid === currentJobRef.current) {
        clearPolling();
        setState(prev => ({
          ...prev,
          isGenerating: false,
          error: null,
        }));

        if (payload.gcs_url) {
          autoDownload(payload.gcs_url);
          onDownloadRef.current?.();
        }

        currentJobRef.current = null;

        // Fetch real cache metadata from backend
        const facilityId = currentFacilityRef.current;
        if (!facilityId) return;
        channel.push('check_report_cache', {
          template: 'non_compliant_bols',
          facility_id: facilityId,
        })
          .receive('ok', (response: any) => {
            setState(prev => ({
              ...prev,
              cacheStatus: {
                status: response.status || 'cached',
                cached_at: response.cached_at,
                age_seconds: response.age_seconds,
                row_count: response.row_count,
                presigned_url: response.presigned_url,
              },
            }));
          });
      }
    });

    // Listen for failures
    channel.on('report_failed', (payload: any) => {
      if (payload.job_uuid === currentJobRef.current) {
        clearPolling();
        setState(prev => ({
          ...prev,
          isGenerating: false,
          error: payload.error_message || 'Report generation failed',
        }));
        currentJobRef.current = null;
      }
    });

    // Listen for progress
    channel.on('report_progress', (payload: any) => {
      if (payload.job_uuid === currentJobRef.current) {
        setState(prev => ({
          ...prev,
          isGenerating: true,
        }));
      }
    });

    // Cleanup
    return () => {
      channel.leave();
      socket.disconnect();
    };
  }, [userId, jwtToken, websocketUrl, autoDownload, clearPolling]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => clearPolling();
  }, [clearPolling]);

  // Start polling for cross-replica case
  const startPolling = useCallback((facilityId: number) => {
    clearPolling();

    pollIntervalRef.current = setInterval(() => {
      pollCountRef.current += 1;

      if (pollCountRef.current >= MAX_POLL_ITERATIONS) {
        clearPolling();
        setState(prev => ({
          ...prev,
          isGenerating: false,
          error: 'Report generation timed out. Please try again.',
        }));
        return;
      }

      const channel = channelRef.current;
      if (!channel) return;

      channel.push('check_report_cache', {
        template: 'non_compliant_bols',
        facility_id: facilityId,
      })
        .receive('ok', (response: any) => {
          if (response.status === 'cached') {
            clearPolling();
            setState(prev => ({
              ...prev,
              isGenerating: false,
              cacheStatus: {
                status: 'cached',
                cached_at: response.cached_at,
                age_seconds: response.age_seconds,
                row_count: response.row_count,
                presigned_url: response.presigned_url,
              },
              error: null,
            }));
            if (response.presigned_url) {
              autoDownload(response.presigned_url);
              onDownloadRef.current?.();
            }
          }
          // If still processing, keep polling
        });
    }, POLL_INTERVAL_MS);
  }, [clearPolling, autoDownload]);

  // Check cache status for a facility
  const checkCache = useCallback((facilityId: number) => {
    const channel = channelRef.current;
    if (!channel) return;

    setState(prev => ({
      ...prev,
      cacheStatus: { status: 'checking' },
      error: null,
    }));

    channel.push('check_report_cache', {
      template: 'non_compliant_bols',
      facility_id: facilityId,
    })
      .receive('ok', (response: any) => {
        setState(prev => ({
          ...prev,
          cacheStatus: {
            status: response.status,
            cached_at: response.cached_at,
            age_seconds: response.age_seconds,
            row_count: response.row_count,
            presigned_url: response.presigned_url,
            job_uuid: response.job_uuid,
            started_at: response.started_at,
          },
        }));

        // If already processing, start polling
        if (response.status === 'processing') {
          setState(prev => ({ ...prev, isGenerating: true }));
          if (response.job_uuid) {
            currentJobRef.current = response.job_uuid;
          }
          startPolling(facilityId);
        }
      })
      .receive('error', (err: any) => {
        console.error('Failed to check report cache:', err);
        setState(prev => ({
          ...prev,
          cacheStatus: { status: 'none' },
          error: 'Failed to check report cache',
        }));
      });
  }, [startPolling]);

  // Generate report
  const generateReport = useCallback((facilityId: number, forceRealtime: boolean) => {
    const channel = channelRef.current;
    if (!channel) {
      setState(prev => ({ ...prev, error: 'Not connected' }));
      return;
    }

    currentFacilityRef.current = facilityId;

    setState(prev => ({
      ...prev,
      isGenerating: true,
      error: null,
    }));

    channel.push('create_report', {
      template: 'non_compliant_bols',
      facility_id: facilityId,
      force_realtime: forceRealtime,
    })
      .receive('ok', (response: any) => {
        if (response.status === 'completed') {
          // Cache hit — auto-download immediately, no need to wait for event
          autoDownload(response.gcs_url);
          onDownloadRef.current?.();
          setState(prev => ({ ...prev, isGenerating: false, error: null }));
        } else {
          // status === "processing" — wait for report_completed event as before
          currentJobRef.current = response.job_uuid;
          setState(prev => ({ ...prev, isGenerating: true }));
          startPolling(facilityId);
        }
      })
      .receive('error', (err: any) => {
        console.error('Failed to create BOL report:', err);
        setState(prev => ({
          ...prev,
          isGenerating: false,
          error: err.reason || err.error_message || 'Failed to create report',
        }));
      });
  }, [startPolling]);

  // Reset state (call on modal close)
  const resetState = useCallback(() => {
    clearPolling();
    currentJobRef.current = null;
    setState({
      isGenerating: false,
      cacheStatus: { status: 'none' },
      error: null,
    });
  }, [clearPolling]);

  return {
    ...state,
    checkCache,
    generateReport,
    resetState,
  };
}
